import axios, { type AxiosInstance } from 'axios';

// Chat message types
export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
}

export interface ChatSession {
  id: string;
  title: string;
  messages: ChatMessage[];
  createdAt: number;
  updatedAt: number;
}

export interface SendMessageRequest {
  type: 'business' | 'visual';
  message: string;
  history?: Array<{ role: string; content: string }>;
  config?: Record<string, unknown>;
}

export interface SendMessageResponse {
  success: boolean;
  data?: {
    response: string;
    config?: Record<string, unknown>;
    html?: string;
  };
  error?: string;
}

// API base URL - 使用环境变量配置
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v2';

// 获取或创建访客ID - 复用api.ts中的逻辑
import { getGuestId as getApiGuestId } from './api';
const getGuestId = getApiGuestId;

// Create axios instance for chat API
const chatClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // 2 minutes for AI responses
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token interceptor
chatClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      // 未登录用户，添加访客ID
      config.headers['X-Guest-ID'] = getGuestId();
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Request interceptor
chatClient.interceptors.request.use(
  (config) => {
    console.log('[ChatService] Request:', config.method?.toUpperCase(), config.url);
    return config;
  },
  (error) => {
    console.error('[ChatService] Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
chatClient.interceptors.response.use(
  (response) => {
    console.log('[ChatService] Response:', response.status, response.config.url);
    return response;
  },
  (error) => {
    console.error('[ChatService] Response error:', error.message);
    return Promise.reject(error);
  }
);

// Retry mechanism
async function withRetry<T>(
  fn: () => Promise<T>,
  retries: number = 2,
  delay: number = 1000
): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    if (retries <= 0) throw error;
    await new Promise((resolve) => setTimeout(resolve, delay));
    return withRetry(fn, retries - 1, delay * 2);
  }
}

export const chatService = {
  /**
   * Send message to Business AI (non-streaming)
   * 使用 /agents/chat 端点
   */
  async sendBusinessMessage(
    message: string,
    history: Array<{ role: string; content: string }> = []
  ): Promise<SendMessageResponse> {
    return withRetry(async () => {
      // 转换历史消息格式
      const messages = [
        ...history.slice(-10),
        { role: 'user', content: message }
      ];
      
      const response = await chatClient.post('/agents/chat', {
        model: 'kimi-8k',
        messages,
        temperature: 0.7,
        maxTokens: 4096,
        stream: false,
      });
      
      return {
        success: true,
        data: {
          response: response.data.content || response.data,
        },
      };
    });
  },

  /**
   * Send message to Visual AI (non-streaming)
   * 使用 /agents/chat 端点
   */
  async sendVisualMessage(
    message: string,
    _config: Record<string, unknown>,
    history: Array<{ role: string; content: string }> = []
  ): Promise<SendMessageResponse> {
    return withRetry(async () => {
      // 转换历史消息格式
      const messages = [
        ...history.slice(-10),
        { role: 'user', content: message }
      ];
      
      const response = await chatClient.post('/agents/chat', {
        model: 'kimi-8k',
        messages,
        temperature: 0.7,
        maxTokens: 4096,
        stream: false,
      });
      
      return {
        success: true,
        data: {
          response: response.data.content || response.data,
        },
      };
    });
  },

  /**
   * Send message with streaming response (unified endpoint)
   * 使用 /agents/chat 端点 (stream模式)
   */
  async sendMessageStream(
    request: SendMessageRequest,
    onChunk: (chunk: string, fullResponse: string) => void
  ): Promise<{ response: string; config?: Record<string, unknown>; html?: string }> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      let fullResponse = '';
      let lastProcessedIndex = 0; // 记录上次处理到的位置，避免重复处理

      // 转换历史消息格式
      const messages = [
        ...(request.history || []).slice(-10),
        { role: 'user', content: request.message }
      ];

      xhr.open('POST', `${API_BASE_URL}/agents/chat`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      
      // Add auth token or guest ID
      const token = localStorage.getItem('token');
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      } else {
        // 未登录用户，添加访客ID
        xhr.setRequestHeader('X-Guest-ID', getGuestId());
      }

      xhr.onprogress = function () {
        // 只处理新到达的数据
        const newData = xhr.responseText.slice(lastProcessedIndex);
        if (!newData) return;
        
        const lines = newData.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') continue;

            try {
              const parsed = JSON.parse(data);
              if (parsed.content) {
                fullResponse += parsed.content;
                onChunk(parsed.content, fullResponse);
              }
              if (parsed.error) {
                reject(new Error(parsed.error));
              }
            } catch {
              // Ignore parse errors for non-JSON data
            }
          }
        }
        
        // 更新已处理位置
        lastProcessedIndex = xhr.responseText.length;
      };

      xhr.onload = function () {
        if (xhr.status >= 200 && xhr.status < 300) {
          // Parse config from response if it's business AI
          let config: Record<string, unknown> | undefined;
          let html: string | undefined;

          if (request.type === 'business') {
            try {
              // Try to extract JSON code block
              const jsonMatch = fullResponse.match(/```(?:json)?\s*([\s\S]*?)```/);
              let jsonText: string;
              
              if (jsonMatch) {
                jsonText = jsonMatch[1].trim();
              } else {
                // Try to extract JSON directly
                const jsonStart = fullResponse.indexOf('{');
                const jsonEnd = fullResponse.lastIndexOf('}');
                if (jsonStart !== -1 && jsonEnd !== -1 && jsonEnd > jsonStart) {
                  jsonText = fullResponse.substring(jsonStart, jsonEnd + 1);
                } else {
                  jsonText = fullResponse.trim();
                }
              }
              
              config = JSON.parse(jsonText);
            } catch {
              // Config parsing failed, but we still have the response
              // 这是正常情况，AI返回的是自然语言而非结构化JSON
              if (import.meta.env.DEV) {
                console.log('[ChatService] No structured config in response (normal for natural language replies)');
              }
            }
          } else if (request.type === 'visual') {
            // Extract HTML code block for visual AI
            const htmlMatch = fullResponse.match(/```html\s*([\s\S]*?)```/);
            if (htmlMatch) {
              html = htmlMatch[1].trim();
            } else {
              html = fullResponse;
            }
          }

          resolve({ response: fullResponse, config, html });
        } else {
          reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
        }
      };

      xhr.onerror = () => reject(new Error('网络请求失败'));
      xhr.ontimeout = () => reject(new Error('请求超时'));

      xhr.send(JSON.stringify({
        model: 'kimi-8k',
        messages,
        temperature: 0.7,
        maxTokens: 4096,
        stream: true,
      }));
    });
  },

  /**
   * Stream business AI message
   */
  async streamBusinessMessage(
    message: string,
    history: Array<{ role: string; content: string }> = [],
    onChunk: (chunk: string, fullResponse: string) => void
  ): Promise<{ response: string; config?: Record<string, unknown> }> {
    const result = await this.sendMessageStream(
      {
        type: 'business',
        message,
        history: history.slice(-10),
      },
      onChunk
    );
    return { response: result.response, config: result.config };
  },

  /**
   * Stream visual AI message
   */
  async streamVisualMessage(
    message: string,
    config: Record<string, unknown>,
    history: Array<{ role: string; content: string }> = [],
    onChunk: (chunk: string, fullResponse: string) => void
  ): Promise<{ response: string; html?: string }> {
    const result = await this.sendMessageStream(
      {
        type: 'visual',
        message,
        config,
        history: history.slice(-10),
      },
      onChunk
    );
    return { response: result.response, html: result.html };
  },
};

export default chatService;
